

























































































import { Vue, Component, Watch } from 'vue-property-decorator';
import basketService from '@/project/basket/basket.service';
import {
    AddressViewObject,
    BasketViewModel,
    CheckoutViewModel,
    DeliveryAddressViewObject,
    DeliveryMethodViewObject,
    PaymentMethodViewObject
} from '@/types/apiServerContract';
import CheckoutHeader from './CheckoutHeader.vue';
import CheckoutFooter from './CheckoutFooter.vue';
import CheckoutAddressOverview from './addresses/CheckoutAddressOverview.vue';
import CheckoutShipping from './CheckoutShipping.vue';
import CheckoutPayment from './CheckoutPayment.vue';
import CheckoutOrderDetails from './CheckoutOrderDetails.vue';
import CheckoutOrderSummary from './CheckoutOrderSummary.vue';
import CheckoutContactInfo from './CheckoutContactInfo.vue';
import Api from '@/project/http/Api.service';
import { PartialUpdateBasketRequest } from '@/project/http/controllers/basketController';
import { OrderDetailsData, ShippingData, Validatable } from './types';
import { debounce } from 'lodash';
import PageToaster from '@/project/ui-components/PageToaster.vue';
import { PageName } from '@/router/routes/constants';
import gtmService from '@/project/tracking/gtm/GTM.service';
import loggingService from '@/core/logging.service';
import dictionaryService from '@/core/translation/dictionary.service';
import AnimateValue from '@/core/ui-components/AnimateValue.vue';
import SpinnerOverlay from '@/project/spinners/SpinnerOverlay.vue';
import { Ensure } from '@/router/Ensure';

@Component({
    components: {
        PageToaster,
        CheckoutContactInfo,
        CheckoutOrderSummary,
        CheckoutOrderDetails,
        CheckoutPayment,
        CheckoutShipping,
        CheckoutAddressOverview,
        CheckoutFooter,
        CheckoutHeader,
        AnimateValue,
        SpinnerOverlay
    }
})
export default class CheckoutPage extends Vue {
    private checkoutModel: CheckoutViewModel | null = null;
    private validationErrors = false;
    private deliveryAddresses: readonly DeliveryAddressViewObject[] = [];
    private debouncedUpdateBasket = debounce(this.updateBasket, 1500);
    private paymentTerminal: string | undefined;
    private initialized = false;
    private loading = false;
    private paymentError: null | { headline: string, description: string } = null;

    private shippingData: ShippingData = {
        isPartDelivery: false,
        postponeDelivery: false,
        shippingMethodCode: undefined,
        postponeShipmentDate: undefined,
        deliveryPhoneNumberRequired: false,
        deliveryEmailRequired: false
    };

    private orderDetailsData: OrderDetailsData = {
        additionalEmailForDeliveryUpdates: '',
        comment: '',
        orderContact: '',
        reference: '',
        deliveryPhoneNumber: ''
    };

    private paymentMethodCode: string | null = null;
    private keywordReference: string | null = null;
    private deliveryAddress: AddressViewObject | DeliveryAddressViewObject | null = null;

    private creditcard: boolean = false;
    private shipping: boolean = true;
    private basketUpdateInProgress: boolean = false;
    private basketUpdateRequest!: PartialUpdateBasketRequest | null;

    async created() {
        if (this.$route.query?.errorMessage) {
            this.paymentError = this.altapayErrorMessage();
        }
        try {
            await basketService.recalcBasket();
            await this.fetchCheckoutData();
            this.initLocals();
        } catch (e) {
            loggingService.exception('Checkoutpage exception', e);
        }
    }

    private redirectAway() {
        this.$router.replace('/');
    }

    private get basketLink(): string {
        return PageName.BASKET;
    }

    private get basket(): BasketViewModel | null {
        return basketService.basketWithCommittedLineItems;
    }

    private get isErpAvailable(): boolean {
        // True as long as we are not certain
        return this.checkoutModel?.isErpServiceAvailable ?? true;
    }

    private altapayErrorMessage(): null | { headline: string, description: string } {
        if (this.$route.query?.errorMessage) {
            const error: string = this.$route.query.errorMessage as string || 'Unknown error';
            return {
                headline: dictionaryService.get('Checkout.Payment.Error.Title'),
                description: dictionaryService.get('Checkout.Payment.Error.Description', [error as string])
            };
        }
        return null;
    }

    @Watch('basket', {
        immediate: true
    })
    private onPaymentBasketChange(value: BasketViewModel, oldValue: BasketViewModel | null) {
        if (!oldValue && value) {
            // Now we have basket - check lineitem count
            if (value.lineItems.length === 0 && value.quotes.length === 0) {
                this.redirectAway();
            } else {
                gtmService.trackCheckoutPage(value.lineItems.concat(...value.quotes.map(x => x.lineItems)));
            }
        }
    }

    private get isReferenceRequired(): boolean {
        return this.checkoutModel?.isReferenceRequired ?? false;
    }

    private get isShippingPhoneNumberRequired(): boolean {
        return this.shippingData?.deliveryPhoneNumberRequired ?? false;
    }

    private get isShippingEmailRequired(): boolean {
        return this.shippingData?.deliveryEmailRequired ?? false;
    }

    private get maxNumberOfShipments(): number {
        // maxNumberOfShipments is an enum: 0 means unlimited. We set it to be -1 that is invalid, it should be loaded from checkout context in a moment.
        return this.checkoutModel?.maxNumberOfShipments ?? -1;
    }

    private get quotes() {
        return this.basket?.quotes ?? [];
    }

    private get isPartDeliveryAllowed(): boolean {
        // If only credit-card as payment option, partdelivery not allowed
        const onlyOne = (this.paymentMethods?.length ?? 0) === 1 ? this.paymentMethods![0] : null;
        const onlyCreditCardPayment = onlyOne?.isCreditCard ?? false;
        const isPartDeliveryAllowed = this.checkoutModel?.isPartDeliveryAllowed ?? false;
        const partDeliveryAllowedInQuote = this.quotes.length > 0 && this.quotes[0].partDelivery;
        return !onlyCreditCardPayment && (isPartDeliveryAllowed || partDeliveryAllowedInQuote);
    }

    private get isPartDeliveryDefault(): boolean {
        return this.checkoutModel?.isPartDeliveryDefault ?? false;
    }

    private get countryCode(): string | null {
        return this.deliveryAddress?.countryCode ?? null;
    }

    private async fetchCheckoutData() {
        const checkoutViewModel = await Api.checkout.get();

        // Potentially many deliveryaddresses - freeze them before Vue adds reactivity
        this.deliveryAddresses = Object.freeze(checkoutViewModel.deliveryAddresses);
        delete (checkoutViewModel as any).deliveryAddresses; // Need any cast to prevent es-lint rule "operand must be optional"

        this.checkoutModel = checkoutViewModel;
    }

    private initLocals(): void {
        this.deliveryAddress = this.basket?.deliveryAddress ?? null;

        this.orderDetailsData = {
            reference: this.basket?.reference ?? '',
            orderContact: this.basket?.orderContact ?? '',
            comment: this.basket?.comment ?? '',
            additionalEmailForDeliveryUpdates: this.basket?.additionalEmailForDeliveryUpdates ?? '',
            deliveryPhoneNumber: this.basket?.deliveryAddress?.phone ?? ''
        };

        const deliveryPhoneNumberRequired = this.shippingMethods?.find(x => x.shippingMethodCode === this.basket?.shippingMethodCode)?.shipmentPhoneMandatory ?? false;
        const deliveryEmailRequired = this.shippingMethods?.find(x => x.shippingMethodCode === this.basket?.shippingMethodCode)?.shipmentEmailMandatory ?? false;

        this.shippingData = {
            postponeShipmentDate: this.basket?.postponeShipmentDate ? new Date(this.basket?.postponeShipmentDate) : undefined,
            postponeDelivery: this.basket?.postponeShipmentDate != null,
            isPartDelivery: this.basket?.isPartDelivery ?? false,
            shippingMethodCode: this.basket?.shippingMethodCode,
            deliveryPhoneNumberRequired: deliveryPhoneNumberRequired,
            deliveryEmailRequired: deliveryEmailRequired
        };

        this.paymentMethodCode = this.basket?.paymentMethodCode ?? null;

        setTimeout(() => this.initialized = true, 200);
    }

    private pushBasketUpdate(partial: PartialUpdateBasketRequest) {
        this.basketUpdateRequest = { ...this.basketUpdateRequest, ...partial };
        // Don't wan't changes due to initialization of sub-components
        if (!this.initialized) return;
        this.basketUpdateInProgress = true;
        this.debouncedUpdateBasket();
    }

    private async updateBasket() {
        // Don't wan't changes due to initialization of sub-components
        if (!this.initialized || !this.basketUpdateRequest) {
            this.basketUpdateInProgress = false;
            return;
        }
        basketService.updateBasket(this.basketUpdateRequest).finally(() => {
            this.basketUpdateInProgress = false;
        });
        this.basketUpdateRequest = null;
    }

    private get invoiceAddress(): AddressViewObject | null {
        return this.basket?.invoiceAddress ?? null;
    }

    @Watch('deliveryAddress')
    private onDeliveryAddressChange(deliveryAddress: DeliveryAddressViewObject) {
        this.validationErrors = false;
        this.isShippingPhoneNumberRequired && (this.orderDetailsData.deliveryPhoneNumber = deliveryAddress.phone);
        this.pushBasketUpdate({
            deliveryAddress
        });
    }

    @Watch('shippingData')
    private onShippingDataChange(shippingData: ShippingData) {
        this.validationErrors = false;
        this.pushBasketUpdate({
            shippingMethodCode: shippingData.shippingMethodCode,
            isPartDelivery: shippingData.isPartDelivery,
            postponeShipmentDate: shippingData.postponeShipmentDate,
            shouldPostponeShipment: shippingData.postponeDelivery,
            updateDeliveryPrice: true
        });
    }

    @Watch('orderDetailsData')
    private onOrderDetailsChange(orderDetailsData: OrderDetailsData) {
        this.validationErrors = false;
        // Will be called every time a character is changed - so debounce.
        // Flush is called on submit-order to include pending changes.
        this.pushBasketUpdate({
            reference: orderDetailsData.reference,
            orderContact: orderDetailsData.orderContact,
            comment: orderDetailsData.comment,
            additionalEmailForDeliveryUpdates: orderDetailsData.additionalEmailForDeliveryUpdates,
            ...(this.isShippingPhoneNumberRequired && { deliveryPhoneNumber: orderDetailsData.deliveryPhoneNumber })
        });
        if (!this.initialized) {
            // Make sure they are send right away and blocked by initialized flag.
            this.debouncedUpdateBasket.flush();
        }
    }

    @Watch('paymentMethodCode')
    private onPaymentMethodCodeChange(paymentMethodCode: string) {
        this.validationErrors = false;
        this.pushBasketUpdate({
            paymentMethodCode: paymentMethodCode
        });
        this.paymentTerminal = this.checkoutModel?.paymentMethods.find(pm => pm.paymentMethodCode === paymentMethodCode)?.terminal;
    }

    private get paymentMethods(): PaymentMethodViewObject[] | null {
        return this.checkoutModel?.paymentMethods ?? null;
    }

    private get shippingMethods(): DeliveryMethodViewObject[] | null {
        return this.checkoutModel?.deliveryMethods ?? null;
    }

    private get shipToCountries(): string[] {
        return this.checkoutModel?.shipToCountries ?? [];
    }

    private gotoConfirmationPage(orderId: string): void {
        this.$router.replace({ query: { orderId }, path: PageName.CONFIRMATION });
    }

    private async placeOrder() {
        this.validationErrors = false;
        this.debouncedUpdateBasket.flush();

        const validations: boolean[] = await Promise.all([
            this.$refs.addresses.validate(),
            this.$refs.orderDetails.validate(),
            this.$refs.shipping.validate(),
            this.$refs.payment.validate()
        ]);
        const anyErrors = validations.some(v => !v);

        if (!anyErrors) {
            this.loading = true;
            try {
                try {
                    const ensure = new Ensure(() => !this.basketUpdateInProgress);
                    await ensure.start();
                } catch (error) {
                    loggingService.error(error);
                }

                const submitResult = await Api.checkout.submitOrder(this.paymentTerminal!);
                if (submitResult.paymentUrl != null) {
                    window.location.href = submitResult.paymentUrl;
                } else {
                    basketService.forceGetBasket();
                    gtmService.trackCheckoutOptions(this.basket?.shippingMethodCode, this.basket?.isPartDelivery ? 'isPartDelivery' : 'notPartDelivery', this.basket?.paymentMethodCode);
                    this.gotoConfirmationPage(submitResult.uniqueIdentifier);
                }
            } catch (error) {
                loggingService.error(error);
                this.loading = false;
            }
        } else {
            this.validationErrors = true;
        }
    }

    private get ctaLabel() {
        return this.creditcard ? dictionaryService.get('Checkout.GoToPayment') : dictionaryService.get('Checkout.PlaceOrder');
    }

    $refs!: Vue['$refs'] & {
        addresses: Validatable,
        shipping: Validatable,
        orderDetails: Validatable,
        payment: Validatable
    }
}
